import React, { useState, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FileText, 
  MapPin, 
  Building2, 
  Users, 
  Calendar,
  Image as ImageIcon,
  Grid3x3,
  FileSignature,
  Trash2,
  Eye,
  Download,
  Move,
  Layers,
  ChevronLeft,
  ChevronRight,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';

// Lateral Header Component (Fixed on right side of each page)
function LateralHeader({ project, pageNumber, totalPages, sheetTitle }) {
  return (
    <div className="absolute right-0 top-0 bottom-0 w-[180px] bg-white border-l-4 border-blue-600 shadow-lg">
      <div className="h-full flex flex-col text-[8px] leading-tight">
        {/* Top Section - Project Info */}
        <div className="border-b-2 border-slate-300 p-2">
          <div className="font-bold text-[9px] mb-1">PROJECT INFO</div>
          <div className="space-y-0.5">
            <div>
              <span className="font-semibold">No:</span> {project.project_number || '273-F/PMB'}
            </div>
            <div className="font-semibold text-[7px] uppercase">
              {project.project_type?.replace(/_/g, ' ') || 'RESIDENCE REMODEL'}
            </div>
          </div>
        </div>

        {/* Owner Section */}
        <div className="border-b-2 border-slate-300 p-2">
          <div className="font-bold text-[9px] mb-1">OWNER</div>
          <div className="space-y-0.5">
            <div className="font-semibold">{project.client_name || 'Owner Name'}</div>
            <div>{project.address || 'Project Address'}</div>
          </div>
        </div>

        {/* Architectural Firm */}
        <div className="border-b-2 border-slate-300 p-2">
          <div className="font-bold text-[9px] mb-1">ARCHITECTURAL</div>
          <div className="space-y-0.5">
            <div className="font-bold">SIDLEY JONES, INC.</div>
            <div>10125 WASHINGTON BLVD.</div>
            <div>CULVER CITY, CA 90232</div>
            <div className="mt-1">
              <div>TEL: (310) 945-3475</div>
              <div>FAX: (323) 935-0358</div>
            </div>
            <div className="mt-1 font-semibold">MICHAEL S. JONES, AIA</div>
            <div>mjones@sidleyjones.com</div>
          </div>
        </div>

        {/* Sheet Info */}
        <div className="border-b-2 border-slate-300 p-2">
          <div className="font-bold text-[9px] mb-1">SHEET</div>
          <div className="space-y-0.5">
            <div className="font-semibold">{sheetTitle || 'COVER SHEET'}</div>
            <div className="flex justify-between mt-2">
              <span className="font-bold">SHEET:</span>
              <span className="font-mono">A{pageNumber}.00</span>
            </div>
            <div className="flex justify-between">
              <span>DATE:</span>
              <span>{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span>SCALE:</span>
              <span>AS NOTED</span>
            </div>
          </div>
        </div>

        {/* Revisions */}
        <div className="border-b-2 border-slate-300 p-2">
          <div className="font-bold text-[9px] mb-1">REVISIONS</div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-black flex items-center justify-center">
              <span className="text-[10px] font-bold">△</span>
            </div>
            <div>
              <div className="font-semibold">PLAN CHECK</div>
              <div>{new Date().toLocaleDateString()}</div>
            </div>
          </div>
        </div>

        {/* Stamp & Signature */}
        <div className="flex-1 flex flex-col items-center justify-center p-2 border-b-2 border-slate-300">
          {/* Real Stamp & Signature Image */}
          <div className="w-full flex items-center justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_archidocs-3/artifacts/o221r7m7_Sello_Firma.png"
              alt="Professional Seal and Signature"
              className="max-w-full h-auto"
              style={{ maxHeight: '120px', width: 'auto' }}
            />
          </div>
          
          {/* Additional Info Below */}
          <div className="w-full text-center mt-2">
            <div className="text-[7px] font-semibold mt-0.5">ARCHITECT</div>
            <div className="text-[7px]">MICHAEL S. JONES</div>
            <div className="text-[6px]">LICENSE: C24740</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Canvas Item Component
function CanvasElement({ element, onUpdate, onRemove, isSelected, onSelect }) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const elementRef = useRef(null);
  
  const handleMouseDown = (e) => {
    if (e.target.closest('.delete-btn')) return;
    
    setIsDragging(true);
    onSelect(element.id);
    
    const rect = elementRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const canvas = document.getElementById('free-canvas-inner');
    if (!canvas) return;
    
    const canvasRect = canvas.getBoundingClientRect();
    
    const newX = e.clientX - canvasRect.left - dragOffset.x;
    const newY = e.clientY - canvasRect.top - dragOffset.y;
    
    onUpdate(element.id, {
      x: Math.max(0, Math.min(newX, canvasRect.width - element.width)),
      y: Math.max(0, Math.min(newY, canvasRect.height - element.height)),
    });
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);
  
  const Icon = element.icon;
  
  return (
    <div
      ref={elementRef}
      className={`absolute cursor-move ${isSelected ? 'ring-2 ring-blue-500' : ''} ${isDragging ? 'opacity-70 z-50' : 'z-10'}`}
      style={{
        left: `${element.x}px`,
        top: `${element.y}px`,
        width: `${element.width}px`,
        minHeight: `${element.height}px`,
      }}
      onMouseDown={handleMouseDown}
    >
      <Card className={`h-full border-2 ${element.borderColor} bg-white dark:bg-slate-800 shadow-lg hover:shadow-xl transition-shadow`}>
        <div className="p-4 h-full flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className={`p-1.5 rounded ${element.bgColor}`}>
                <Icon className={`h-4 w-4 ${element.iconColor}`} />
              </div>
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide">
                {element.label}
              </span>
            </div>
            
            <div className="flex items-center space-x-1">
              <Move className="h-3 w-3 text-slate-400" />
              <button
                className="delete-btn p-1 hover:bg-red-50 rounded text-red-500"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(element.id);
                }}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 text-sm">
            {element.renderContent ? element.renderContent() : (
              <p className="text-slate-700 dark:text-slate-300 font-medium">
                {element.preview}
              </p>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

const VisualEditor = ({ project }) => {
  const { t } = useLanguage();
  const [pages, setPages] = useState([
    { id: 1, elements: [], title: 'COVER SHEET' }
  ]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  
  const currentPage = pages[currentPageIndex];
  
  const sensors = [];

  const elementTemplates = [
    {
      id: 'project-header',
      icon: FileText,
      label: t('projectHeader'),
      borderColor: 'border-blue-300',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      width: 600,
      height: 120,
      preview: project.name || 'Project Name',
      renderContent: () => (
        <div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1">
            {project.name || 'Project Name'}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            <MapPin className="inline h-4 w-4 mr-1" />
            {project.address || 'Project Address'}
          </p>
          <p className="text-xs text-slate-500 mt-2">
            {project.project_type?.replace(/_/g, ' ').toUpperCase() || 'PROJECT TYPE'}
          </p>
        </div>
      ),
    },
    {
      id: 'client-info',
      icon: FileSignature,
      label: t('clientInfo'),
      borderColor: 'border-purple-300',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      width: 280,
      height: 140,
      preview: 'Client information',
      renderContent: () => (
        <div className="space-y-2">
          <div>
            <p className="text-xs text-slate-500">Client Name</p>
            <p className="font-semibold text-slate-900 dark:text-white">
              {project.client_name || 'Client Name'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Contact</p>
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {project.client_phone || 'Phone'}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'project-team',
      icon: Users,
      label: t('projectTeam'),
      borderColor: 'border-orange-300',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      width: 400,
      height: 250,
      preview: 'Team members',
      renderContent: () => (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-600 mb-2">
            {project.consultants?.length || 0} Team Members
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {project.consultants?.slice(0, 4).map((member, idx) => (
              <div key={idx} className="p-2 bg-slate-50 dark:bg-slate-900 rounded">
                <p className="font-semibold text-slate-900 dark:text-white">{member.name}</p>
                <p className="text-slate-500">{member.role}</p>
              </div>
            ))}
            {(!project.consultants || project.consultants.length === 0) && (
              <p className="text-slate-400 col-span-2">No team members added</p>
            )}
          </div>
        </div>
      ),
    },
  ];

  const addNewPage = () => {
    const newPage = {
      id: pages.length + 1,
      elements: [],
      title: `SHEET ${pages.length + 1}`
    };
    setPages([...pages, newPage]);
    setCurrentPageIndex(pages.length);
    toast.success('New page added');
  };

  const handleAddElement = (template) => {
    const pageElements = currentPage.elements;
    if (pageElements.find(el => el.id === template.id)) {
      toast.info('Element already on this page');
      return;
    }
    
    const newElement = {
      ...template,
      x: 50,
      y: 50 + (pageElements.length * 30),
    };
    
    const updatedPages = pages.map((page, idx) =>
      idx === currentPageIndex
        ? { ...page, elements: [...page.elements, newElement] }
        : page
    );
    
    setPages(updatedPages);
    setSelectedId(newElement.id);
    toast.success('Element added to page');
  };

  const handleUpdateElement = (id, updates) => {
    const updatedPages = pages.map((page, idx) =>
      idx === currentPageIndex
        ? {
            ...page,
            elements: page.elements.map(el => 
              el.id === id ? { ...el, ...updates } : el
            )
          }
        : page
    );
    setPages(updatedPages);
  };

  const handleRemoveElement = (id) => {
    const updatedPages = pages.map((page, idx) =>
      idx === currentPageIndex
        ? { ...page, elements: page.elements.filter(el => el.id !== id) }
        : page
    );
    setPages(updatedPages);
    if (selectedId === id) setSelectedId(null);
    toast.success('Element removed');
  };

  const handlePreview = () => {
    toast.info('Preview Mode', {
      description: 'This would show a print-ready preview of all pages',
    });
  };

  const handleExport = () => {
    toast.info('Export to PDF', {
      description: `This would generate a PDF with ${pages.length} pages`,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {t('visualDocumentBuilder')}
          </h2>
          <p className="text-slate-600 dark:text-slate-400">
            Multi-page document builder with fixed lateral header
          </p>
        </div>
        
        <div className="flex space-x-2">
          <Button variant="outline" onClick={handlePreview}>
            <Eye className="mr-2 h-4 w-4" />
            {t('preview')}
          </Button>
          <Button onClick={handleExport} className="bg-blue-600 hover:bg-blue-700">
            <Download className="mr-2 h-4 w-4" />
            {t('exportPDF')}
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="p-4 border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10">
        <div className="flex items-start space-x-3">
          <Layers className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
              Multi-Page Document Builder with Fixed Lateral Header
            </h3>
            <p className="text-sm text-slate-700 dark:text-slate-300">
              Each page includes a professional lateral header with project info, architectural firm details, and sheet information. Add elements to build your document, navigate between pages, and export to PDF.
            </p>
          </div>
        </div>
      </Card>

      {/* Toolbar */}
      <Card className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 mr-2">
            {t('addElements')}:
          </span>
          {elementTemplates.map((template) => {
            const Icon = template.icon;
            const isAdded = currentPage.elements.find(el => el.id === template.id);
            return (
              <Button
                key={template.id}
                variant="outline"
                size="sm"
                onClick={() => handleAddElement(template)}
                disabled={isAdded}
                className={`flex items-center space-x-2 ${isAdded ? 'opacity-50' : ''}`}
              >
                <Icon className="h-4 w-4" />
                <span className="text-xs whitespace-nowrap">{template.label}</span>
              </Button>
            );
          })}
        </div>
      </Card>

      {/* Page Navigation */}
      <Card className="p-4 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPageIndex(Math.max(0, currentPageIndex - 1))}
              disabled={currentPageIndex === 0}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Page {currentPageIndex + 1} of {pages.length}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPageIndex(Math.min(pages.length - 1, currentPageIndex + 1))}
              disabled={currentPageIndex === pages.length - 1}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={addNewPage}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Page
          </Button>
        </div>
      </Card>

      {/* Canvas with Lateral Header */}
      <div className="relative">
        <Card className="border-2 border-slate-300 dark:border-slate-600 overflow-hidden">
          <div className="relative bg-white dark:bg-slate-900" style={{ height: '800px' }}>
            {/* Main Canvas Area */}
            <div
              id="free-canvas-inner"
              className="absolute inset-0 pr-[180px]"
              style={{
                backgroundImage: 'radial-gradient(circle, #e2e8f0 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
              onClick={() => setSelectedId(null)}
            >
              {/* Page outline */}
              <div className="absolute inset-8 border border-dashed border-slate-300 dark:border-slate-600 pointer-events-none">
                <div className="absolute top-2 left-2 text-xs text-slate-400">
                  {currentPage.title}
                </div>
              </div>
              
              {currentPage.elements.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pr-[180px]">
                  <div className="text-center">
                    <Layers className="mx-auto h-16 w-16 text-slate-300 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-400 mb-2">
                      {t('emptyCanvas')}
                    </h3>
                    <p className="text-sm text-slate-500">
                      Add elements from toolbar above
                    </p>
                  </div>
                </div>
              )}
              
              {currentPage.elements.map((element) => (
                <CanvasElement
                  key={element.id}
                  element={element}
                  onUpdate={handleUpdateElement}
                  onRemove={handleRemoveElement}
                  isSelected={selectedId === element.id}
                  onSelect={setSelectedId}
                />
              ))}
            </div>
            
            {/* Fixed Lateral Header */}
            <LateralHeader 
              project={project}
              pageNumber={currentPageIndex}
              totalPages={pages.length}
              sheetTitle={currentPage.title}
            />
          </div>
        </Card>
        
        {currentPage.elements.length > 0 && (
          <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded">
            <p className="text-sm text-green-800 dark:text-green-300">
              ✓ <span className="font-semibold">{currentPage.elements.length} elements</span> on page {currentPageIndex + 1}. 
              {t('dragToArrange')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualEditor;
